import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { FitnessState, DeclaredState } from '@cf-shared/data-model/user-state';

/**
 * The three documents a workout exercise build cannot be done without.
 *
 * `WorkoutLoadingContext` in `workout-loader.service.ts` declares all three as present,
 * and every honest caller does supply them. But a page that assembles the context out of
 * fields a LIFECYCLE HOOK was supposed to fill can hand over undefined, because cs-nav
 * calls `ionViewDidEnter` on a page still on screen whether or not `ionViewWillEnter`
 * finished (a page already destroyed is skipped since df-53c1d46615c2) — see
 * `design-docs/boot-sequence.md`, the L8 row, and `design-docs/navigation.md`,
 * "Per-page pattern: defer a heavy body build off the enter slide". That is how
 * df-7b2e93466836 happened.
 *
 * This lives in its own module, away from the Angular service, for two reasons. A plain
 * node unit test can import it (the service pulls in Angular and cannot be loaded that
 * way), and the page that guards BEFORE the build and the loader that refuses INSIDE it
 * ask the same question from one place — two hand-written copies of the same list would
 * drift, and the page would then wave through a context the loader rejects.
 */
export interface WorkoutLoadingDocuments {
  plan?: FitnessPlan;
  fitnessState?: FitnessState;
  declaredState?: DeclaredState;
}

/**
 * Names the documents that are missing, in a fixed order, or an empty array when all
 * three are there. Names rather than a boolean: a report or an error message that says
 * WHICH document is absent tells a future debugger which load did not finish, and
 * "Cannot read properties of undefined" told them nothing at all.
 */
export function namesOfMissingWorkoutLoadingDocuments(documents: WorkoutLoadingDocuments): string[] {
  const missing: string[] = [];
  if (!documents.plan) missing.push('plan');
  if (!documents.fitnessState) missing.push('fitnessState');
  if (!documents.declaredState) missing.push('declaredState');
  return missing;
}
